import { doc, getDoc, writeBatch } from 'firebase/firestore';
import { ParentContext } from 'global/cnr/contexts/ParentContext';
import { _cacheGroupTypes } from 'global/cnr/contexts/useHelpers/useTimestamp';
import { dataFix } from 'global/common/dataFix';
import { createRefPath_event, createRefPath_events } from 'global/firestoreData/appData/appRefPaths';
import _ from 'lodash';
import { useCallback, useContext, useRef, useState } from 'react';
import { SportsSeasonContext } from '../cnr/contexts/SportsSeasonContext';
import { seasonalUpdateTypes } from '../cnr/reducers/reducerHelpers/seasonalDataHelpers';
import { seasonalHelpers } from '../cnr/reducers/reducerHelpers/seasonalHelpers';
import { _allowSportsUpdates, useSeasonalData, useSeasonals } from './useSeasonals';
import { logBatch } from 'global/cnr/reducers/reducerHelpers/logging';
import { _appVersion } from 'global/cnr/contexts/useHelpers/useServiceWorker';
import { useMultiDoc } from 'global/cnr/contexts/useHelpers/useMultiDoc';
import { dataItems_sportsSeason_create } from '../cnr/reducers/SportsSeasonReducer';
import { convertHelpers } from 'global/common/convert';

export const useSportsSeasonData = () => {

  const { handleGet_multiDocs } = useMultiDoc()

  const parentContext = useContext(ParentContext)
  const { fns, states } = parentContext ?? {}
  const { appUser_state, app_state, paps_state } = states ?? {}
  const { mainDb } = app_state ?? {}
  const { appUser } = appUser_state ?? {}
  const { pathViews } = paps_state ?? {}
  const { database_fns } = fns

  const { sportsSeason_state } = SportsSeasonContext ?? {}
  const { sportPermissions } = sportsSeason_state ?? {}

  const { ammend_updateProps, ammend_updateNameProps } = useSeasonals()
  const { update_seasonalOrGlobal, getUpdateProps, getSports_collectionItem } = useSeasonalData()

  const [updating, setUpdating] = useState()

  const datasToUpdateRef = useRef({
    seasonal: {},
    teams: {},
    matches: {}
  })

  const update_modifiedMatches = useCallback(async ({ groupedMatchesToUpdate, opts }) => {

    setUpdating(true)

    const { selectedYear, sportsKey } = opts ?? {}
    const _pks = seasonalHelpers.getPks(pathViews, selectedYear, sportsKey, ['teams'])
    const _sportsYearCollectionItem = await getSports_collectionItem(_pks, '_matches_seasonal', true)

    if (_sportsYearCollectionItem && !_.isEmpty(_sportsYearCollectionItem)) {
      const { _itemKey: sycKey } = _sportsYearCollectionItem
      const _docKey = sycKey
      const batch = writeBatch(mainDb);
      _.forEach(groupedMatchesToUpdate, (gms, levelKey) => {
        const _subPath = createRefPath_event(pathViews, ['_matches_seasonal', _docKey, 'matches', levelKey]);
        const _doc = doc(mainDb, _subPath)
        console.log('subPath', _subPath, gms)
        batch.update(_doc, gms)
      })
      if (_allowSportsUpdates.modifications) {
        const _timestamp = Date.now()
        database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonMatchesInfo, pathViews.sports, _timestamp, batch)
        database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonResultsInfo, pathViews.sports, _timestamp, batch)
        await batch.commit();
        console.log('update_modifiedMatches - SUCCESS')
      }
      setUpdating(false)
    }
  }, [])

  const update_modifiedTeams = useCallback(async ({ teamsToUpdate, opts, callback }) => {

    const { selectedYear, sportsKey } = opts ?? {}
    const _pks = seasonalHelpers.getPks(pathViews, selectedYear, sportsKey)
    const _sportsYearCollectionItem = await getSports_collectionItem(_pks, '_teams_seasonal', true)

    if (_sportsYearCollectionItem && !_.isEmpty(_sportsYearCollectionItem)) {
      const { _itemKey: sycKey } = _sportsYearCollectionItem
      const _docKey = sycKey
      const batch = writeBatch(mainDb);
      const _subPath = createRefPath_event(pathViews, ['_teams_seasonal', _docKey]);
      const _doc = doc(mainDb, _subPath)
      console.log('subPath', _subPath, teamsToUpdate)
      batch.update(_doc, teamsToUpdate)
      if (_allowSportsUpdates.modifications) {
        const _timestamp = Date.now()
        database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonInfo, pathViews.sports, _timestamp, batch)
        await batch.commit();
      }
    }
  }, [])

  const ammend_sportsSeasonalItem = useCallback((seasonal) => {
    datasToUpdateRef.current.seasonal = seasonal
    return seasonal
  }, [])

  const ammend_seasonalData = useCallback((payload) => {
    datasToUpdateRef.current.seasonal = payload
    return payload
  }, [])

  const ammend_sportsTeams = useCallback((teams) => {
    datasToUpdateRef.current.teams = teams
    return teams
  }, [])

  const ammend_sportsSeasonDataToUpdate = useCallback(
    ({ matches, seasonal, teams }) => {
      datasToUpdateRef.current = {
        matches,
        seasonal,
        teams
      }
      return datasToUpdateRef.current
    },
    []
  )

  const update_seasonSportsData = useCallback(
    ({ callback }) => {
      return updateSportsData(
        database_fns,
        mainDb,
        pathViews,
        datasToUpdateRef.current,
        appUser,
        callback
      )
    },
    []
  )

  const update_seasonalData = useCallback(async ({ updateType, opts, callback }) => {
    updateSeasonalUpdate({
      appUser,
      callback,
      database_fns,
      getSports_collectionItem,
      mainDb,
      opts,
      pathViews,
      sportPermissions,
      updateType,
    })
  }, []);

  const update_seasonalMatches = useCallback(async ({ matchesToUpdate, groupedMatchesToUpdate, opts }) => {
    setUpdating(true)
    const cb = () => {
      setUpdating(false)
    }
    updateSeasonMatches({
      appUser,
      callback: cb,
      database_fns,
      getSports_collectionItem,
      mainDb,
      opts,
      pathViews,
      sportPermissions,
      matchesToUpdate,
      groupedMatchesToUpdate,
    })
  }, []);

  const reset = useCallback(() => {
    datasToUpdateRef.current = {
      seasonal: {},
      teams: {},
      matches: {}
    }
  }, [])

  const createSeasonFromPrevious = useCallback((sportSeasonals, selectedYear, homeAways, callback) => {
    const _previousYear = selectedYear - 1
    const _previousSeason = sportSeasonals[_previousYear]
    if (_previousSeason) {
      const { teams, sectionCounts, details, playoffQualifyCounts, parentKeys, sectionDates } = _previousSeason ?? {}
      parentKeys.sportsYears = selectedYear

      let _sectionDates = {};

      if (sectionDates) {
        _sectionDates = ammendSectionDates(sectionDates, selectedYear)
      }

      if (details) {
        _.forEach(details, (detail, detailKey) => {
          if (detail && detailKey.toLowerCase().indexOf('date') > 0 && detailKey.endsWith('Date')) {
            details[detailKey] = convertDateToNewYear(detail, selectedYear)
          }
        })
      }

      const _newSeason = { teams, sectionCounts, details, playoffQualifyCounts, parentKeys, sectionDates: _sectionDates, homeAways }

      sportSeasonals[selectedYear] = { ..._newSeason }
      callback(sportSeasonals, selectedYear)
    }
  }, [])

  const get_seasonalData = useCallback(({ selectedYear, callback }) => {
    if (selectedYear) {
      const cbn = (seasonalData, _parentDataKeys) => {
        const { _seasonalData } = createSeasonalData({ selectedYear, seasonalData, _parentDataKeys })
        callback({ seasonalData, _seasonalData, _parentDataKeys })
      }
      const _pks = seasonalHelpers.getPks(pathViews, selectedYear, pathViews.sports)

      // clear the data by passing in nulls
      // createSportsSeason_handlers.handleStartUpdate()
      // createSportsSeason_handlers.handleAmmend_sportsSeason_selected()

      // get the data for the sason
      handleGet_multiDocs({
        key: 'sportsInfo',
        sportsDocs: dataItems_sportsSeason_create,
        pathViews,
        itemKey: pathViews.sports,
        pks: _pks,
        callback: cbn
      })
    }
  }, [])

  return {
    ammend_seasonalData,
    ammend_sportsSeasonalItem,
    ammend_sportsSeasonDataToUpdate,
    ammend_sportsTeams,
    createSeasonFromPrevious,
    datasToUpdate: datasToUpdateRef.current,
    get_seasonalData,
    reset,
    update_modifiedMatches,
    update_modifiedTeams,
    update_seasonalData,
    update_seasonalMatches,
    update_seasonSportsData,
    updating
  }
}

const ammendSectionDates = (sectionDates, selectedYear) => {
  const _sectionDates = {}
  _.forEach(sectionDates, (sds, sdsKey) => {
    const newDates = sds.map(d =>
      convertDateToNewYear(d, selectedYear)
    );
    _sectionDates[sdsKey] = newDates
  })
  return _sectionDates
}

const updateSeasonMatches = async (
  {
    database_fns,
    mainDb,
    pathViews,
    matchesToUpdate,
    groupedMatchesToUpdate,
    appUser,
    opts,
    getSports_collectionItem,
    callback
  }
) => {

  const { selectedYear, sportsKey } = opts ?? {}

  const _pks = seasonalHelpers.getPks(pathViews, selectedYear, sportsKey)
  const _sportsYearCollectionItem = await getSports_collectionItem(_pks, '_matches_seasonal', true)

  if (_sportsYearCollectionItem && !_.isEmpty(_sportsYearCollectionItem)) {
    const { _itemKey: sycKey } = _sportsYearCollectionItem
    const _docKey = sycKey
    updateTheMatches({
      matches: matchesToUpdate,
      matches_grouped: groupedMatchesToUpdate,
      mainDb,
      database_fns,
      merge: true,
      updateType: seasonalUpdateTypes.fullSeason_matches,
      pathViews,
      sportCollectionName: '_matches_seasonal',
      _docKey,
      callback,
      _pks
    })
  }

}

const updateSportsData = async (
  database_fns,
  mainDb,
  pathViews,
  datasToUpdate,
  appUser,
  callback,
) => {
  const { displayName, uid } = appUser ?? {}

  const status = {
    uid,
    displayName,
    appVersion: _appVersion,
    lastUpdate: _timestamp
  }

  const allows = {
    matches_seasonal: !_.isEmpty(datasToUpdate?.matches_seasonal),
    seasonals: !_.isEmpty(datasToUpdate?.seasonals),
    teams_seasonal: !_.isEmpty(datasToUpdate?.teams_seasonal)
  }

  const paths = {
    matches_seasonal: allows.matches_seasonal && createRefPath_events(pathViews, ['_matches_seasonal']),
    seasonals: allows.seasonals && createRefPath_events(pathViews, ['_seasonals']),
    teams_seasonal: allows.views && createRefPath_events(pathViews, ['_teams_seasonal'])
  };

  const batch = writeBatch(mainDb);

  // (refPath, data, callback, batch, appUserAccess, storagePaths, vit) => fs_update_doc(mainDb, refPath, data, callback, batch, appUserAccess, storagePaths, vit)
  if (allows.matches_seasonal && paths.matches_seasonal) {
    database_fns.update_doc(paths.matches_seasonal, datasToUpdate.matches_seasonal, null, batch);
  }
  if (allows.seasonals && paths.seasonals) {
    database_fns.update_doc(paths.seasonals, datasToUpdate.seasonals, null, batch);
  }
  if (allows.views && paths.views) {
    database_fns.update_doc(paths.views, datasToUpdate.teams_seasonal, null, batch);
  }

  console.group('updateSettings_updatesOnly', baseRef)
  console.log('datasToUpdate', datasToUpdate)
  console.log('paths', paths)
  console.log('status', status)

  _.forEach(batch?._mutations, (m) => {
    if (m?.key?.path?.segments) {
      console.log('segments', m?.key?.path?.segments)
    }
  })

  console.groupEnd()

  if (_allowSportsUpdates.seasonals) {
    const _timestamp = Date.now()
    database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonInfo, pathViews.sports, _timestamp, batch)
    batch.commit();
  }

  if (callback) {
    callback();
  }

}

const ammendMatches = (matches) => {
  const _matches = _.groupBy(matches, '_matchCollectionKey')
  console.log('_matches', _matches)
}

const groupMatchesByCollectionKey = (matches) =>
  _.transform(
    matches,
    (acc, match, matchKey) => {
      const groupKey = match._matchCollectionKey;
      if (!groupKey) return;

      if (!acc[groupKey]) acc[groupKey] = {};
      acc[groupKey][matchKey] = match;
    },
    {}
  );


const updateSeasonalUpdate = async ({
  appUser,
  callback,
  database_fns,
  getSports_collectionItem,
  mainDb,
  opts,
  pathViews,
  sportPermissions,
  updateType,
}) => {

  const {
    _sportsAbr,
    selectedYear,
    sportsKey,
    sportsSeason_selected,
  } = opts ?? {}

  const {
    seasonals: {
      details,
      playoffQualifyCounts,
      matches,
      sectionDates,
      crossSectionDates,
      homeAways,
      teams
    } } = sportsSeason_selected ?? {}

  let sportCollectionName = ''
  let merge = false
  let updateParentDoc = false;

  switch (updateType) {
    case seasonalUpdateTypes.fullSeason_teams:
      sportCollectionName = '_teams_seasonal'
      break;

    case seasonalUpdateTypes.fullSeason_matches:
      sportCollectionName = '_matches_seasonal'
      merge = false
      break;

    default:
      sportCollectionName = '_seasonals'
  }

  if (!selectedYear) {
    callback()
    return;
  }

  const _sportsDocPrefix = `${_sportsAbr}_${selectedYear}`
  const _pks = seasonalHelpers.getPks(pathViews, selectedYear, sportsKey)
  const _sportsYearCollectionItem = await getSports_collectionItem(_pks, sportCollectionName, true)

  let _docKey;
  let _updateParentDoc = false;
  let _isNew = false;

  if (_sportsYearCollectionItem && !_.isEmpty(_sportsYearCollectionItem)) {
    const { _itemKey: sycKey } = _sportsYearCollectionItem
    _docKey = sycKey
  } else {
    _docKey = _sportsDocPrefix + '_' + database_fns.get_docKey()
    _isNew = true
  }

  let dataToUpdate;

  switch (updateType) {
    case seasonalUpdateTypes.fullSeason_teams:
    case seasonalUpdateTypes.teams:
      dataToUpdate = { teams: teams }
      break;

    case seasonalUpdateTypes.fullSeason_matches:
      dataToUpdate = { matches: matches }
      _updateParentDoc = true
      break;

    case seasonalUpdateTypes.details:
      dataToUpdate = { details: details }
      if (playoffQualifyCounts) {
        dataToUpdate.playoffQualifyCounts = playoffQualifyCounts
      }
      break;

    case seasonalUpdateTypes.sectionCrossovers:
      break;

    case seasonalUpdateTypes.sectionDates:
      dataToUpdate = { sectionDates, crossSectionDates }
      break;

    case seasonalUpdateTypes.matches:
      dataToUpdate = { matches: matches }
      if (homeAways) {
        dataToUpdate.homeAways = homeAways
      }
      break;

    case seasonalUpdateTypes.homeAways:
      dataToUpdate = { homeAways: homeAways }
      break;

    default:
    // nothing
  }

  if (_isNew && dataToUpdate) {
    dataToUpdate.parentKeys = { ..._pks }
  }

  if (!dataToUpdate) {
    callback()
    return;
  }

  dataToUpdate = dataFix.cleanForFirebase(dataToUpdate, { removeUnderscore: true })

  const batch = writeBatch(mainDb);

  const refPath = createRefPath_event(pathViews, [sportCollectionName, _docKey])

  const paths = []

  if (refPath) {

    switch (updateType) {
      case seasonalUpdateTypes.fullSeason_matches:
      case seasonalUpdateTypes.matches:
        if (matches) {
          updateTheMatches({
            matches: matches,
            mainDb,
            database_fns,
            merge,
            updateType: updateType,
            pathViews,
            sportCollectionName: sportCollectionName,
            _docKey,
            callback,
            _pks,
            _updateParentDoc,
            _isNew
          })
        }
        return;

      default:
        if (!_.isEmpty(dataToUpdate)) {
          if (_isNew) {
            database_fns.set_doc(refPath, dataToUpdate, true, false, batch);
          } else {
            database_fns.update_doc(refPath, dataToUpdate, null, batch);
          }
          paths.push({
            path: refPath,
            data: dataToUpdate
          })
        }
    }
  }

  console.group('updateSeasonalUpdate', updateType)
  console.log('_isNew', _isNew)
  console.log('refPath', refPath)
  console.log('paths', paths)
  console.groupEnd()

  logBatch(batch, dataToUpdate)

  if (_allowSportsUpdates.seasonals) {
    const _timestamp = Date.now()
    database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonInfo, pathViews.sports, _timestamp, batch)
    batch.commit();
  }

  callback()

}

const updateTheMatches = async ({
  matches,
  matches_grouped,
  mainDb,
  database_fns,
  merge,
  _docKey,
  updateType,
  pathViews,
  sportCollectionName,
  callback,
  _pks,
  _updateParentDoc,
  _isNew,
  _mainDb
}) => {

  if (matches || matches_grouped) {
    const paths = []

    let groupedMatches;

    if (matches_grouped) {
      groupedMatches = matches_grouped
    } else {
      seasonalHelpers.ammend_matchLevelsAndSections(matches)
      groupedMatches = groupMatchesByCollectionKey(matches)
    }

    const groupedEntries = _.toPairs(groupedMatches);

    console.group('updateTheMatches')
    console.log('updateType', updateType)
    console.log('_isNew', _isNew)

    if (_isNew) {
      const batch_main = writeBatch(mainDb);

      const _docPath = createRefPath_event(pathViews, [sportCollectionName, _docKey]);

      const _dataToUpdate_pks = {
        parentKeys: { ..._pks }
      }

      // refPath, data, merge, dispatch, batch, altDb
      database_fns.set_doc(
        _docPath,
        _dataToUpdate_pks,
        true,
        false,
        batch_main
      );

      if (_allowSportsUpdates.matches) {
        await batch_main.commit();
      }

      console.log('_docPath', _docPath)
      console.log('_dataToUpdate_pks', _dataToUpdate_pks)
      logBatch(batch_main, _dataToUpdate_pks)
    }

    for (const chunk of _.chunk(groupedEntries, 450)) {

      const batch = writeBatch(mainDb);

      _.forEach(chunk, ([groupKey, groupMatches]) => {

        switch (groupKey) {
          case 'non':
            return;
          default:
          // nothing
        }

        let _groupMatches;

        switch (updateType) {
          case seasonalUpdateTypes.fullSeason_matches:
            _groupMatches = _.mapValues(groupMatches, (match) =>
              _.pick(match, [
                'away',
                'home',
                'startDate',
                'startTime',
                'teams',
                '_matchCollectionKey',
                'setCount',
                'sections',
                'levels',
                'bestOf',
              ])
            );
            break;

          case seasonalUpdateTypes.matches:
            _groupMatches = _.mapValues(groupMatches, (match) =>
              _.pick(match, [
                'away',
                'home',
                'startDate',
                'startTime',
                'teams',
                '_matchCollectionKey'
              ])
            );
            break;
        }

        const _subPath = createRefPath_event(pathViews, [sportCollectionName, _docKey, 'matches', groupKey]);

        paths.push({
          path: _subPath,
          matches: _groupMatches
        });

        const _dataToUpdate_matches = {
          matches: _groupMatches
        }

        console.group('level match update', groupKey, merge, _.size(_groupMatches))
        console.log('_subPath', _subPath)
        console.log('_dataToUpdate_matches', _dataToUpdate_matches)
        console.groupEnd()


        const _doc = doc(mainDb, _subPath)
        batch.set(_doc, _dataToUpdate_matches, { merge: merge })

      });

      logBatch(batch)



      if (_allowSportsUpdates.matches) {
        const _timestamp = Date.now()
        database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonMatchesInfo, pathViews.sports, _timestamp, batch)
        database_fns.update_timestamp(_cacheGroupTypes.sportsSeasonResultsInfo, pathViews.sports, _timestamp, batch)
        await batch.commit();
      }

      callback()
    }
  }

}

const parseDate = (str) => {
  const [month, day, year] = str.split('/').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (date) => {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const getNthWeekdayOfMonth = ({ year, month, weekday, nth }) => {
  let count = 0;

  for (let day = 1; day <= 31; day++) {
    const date = new Date(year, month, day);
    if (date.getMonth() !== month) break;

    if (date.getDay() === weekday) {
      count++;
      if (count === nth) return date;
    }
  }

  // fallback → last occurrence if nth doesn't exist
  let lastMatch = null;
  for (let day = 1; day <= 31; day++) {
    const date = new Date(year, month, day);
    if (date.getMonth() !== month) break;

    if (date.getDay() === weekday) {
      lastMatch = date;
    }
  }

  return lastMatch;
};

const convertDateToNewYear = (dateStr, newYear) => {
  const d = parseDate(dateStr);

  const month = d.getMonth();
  const weekday = d.getDay();

  // determine nth occurrence
  let nth = 0;
  for (let day = 1; day <= d.getDate(); day++) {
    const temp = new Date(d.getFullYear(), month, day);
    if (temp.getDay() === weekday) nth++;
  }

  const newDate = getNthWeekdayOfMonth({
    year: newYear,
    month,
    weekday,
    nth
  });

  return formatDate(newDate);
};

const createSeasonalData = ({ selectedYear, seasonalData, _parentDataKeys }) => {

  // clear the data and return
  if (!seasonalData) {
    return {
      sportsSeason_selected: null,
      sportsSeason_selectedPdks: {},
      updating: false
    }
  }

  const { seasonals, teams } = seasonalData ?? {}
  const { teams: _teams } = seasonals ?? {}

  if (teams) { convertHelpers.createItemKeys(teams) }
  if (_teams) { convertHelpers.createItemKeys(_teams) }

  if (seasonals) {
    seasonals.teams = teams || _teams
    seasonals.sectionTeamCounts = groupTeamsByLevelsAndSections(seasonals.teams)
    if (!seasonals.playoffQualifyCounts) {
      seasonals.playoffQualifyCounts = {
        4: 4,
        5: 4,
        6: 4,
        7: 4,
        8: 4,
        9: 4,
      }
    }
  }

  const _seasonalData = { seasonals: seasonals }

  return { _seasonalData }

}

const groupTeamsByLevelsAndSections = (teams) => {
  // Group teams by level and then by section
  const groupedTeams = _.groupBy(teams, 'levels');
  const levels = _.mapValues(groupedTeams, (teamsInLevel) =>
    _.groupBy(teamsInLevel, 'sections')
  );

  const sectionCounts = [];

  // Iterate over levels and sections
  _.forEach(levels, (sections) => {
    _.forEach(sections, (teamsInSection) => {
      const numTeams = teamsInSection.length;
      // Add the number of teams to sectionCounts if it doesn't already exist
      if (!sectionCounts.includes(numTeams)) {
        sectionCounts.push(numTeams);
      }
    });
  });

  return sectionCounts.sort((a, b) => a - b);

};